import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, Stack, Stepper, Step, StepLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Chip, LinearProgress, ToggleButton, ToggleButtonGroup,
  Checkbox
} from '@mui/material';
import {
  FormatListBulleted as ListIcon,
  Inventory2 as CatalogIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import articuloXListaPrecioService, { ArticuloXListaPrecio } from '../../services/articuloXListaPrecioService';
import productoService, { PARAMS_PRODUCTO_OPERATIVO, Producto } from '../../services/productoService';
import ListaPrecioSearch from '../../components/ListaPrecioSearch';

type FuenteType = 'lista' | 'catalogo';

interface PreviewItem {
  codigoArticulo: string;
  descripcion: string;
  /** Base para el precio de venta en la lista (precio lista origen o precio neto del catálogo). */
  costoBase?: number;
  /** Solo catálogo: costo de stock para ArticuloXListaPrecio.precioCosto. */
  precioCostoCatalogo?: number;
  precioCalculado?: number;
  porcentajeCalculado?: number;
  modo: 'PRECIO' | 'PORCENTAJE';
  incluido: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  codigoListaDestino: string;
  onSuccess: () => void;
}

const AgregarArticulosMasivoDialog: React.FC<Props> = ({ open, onClose, codigoListaDestino, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [step, setStep] = useState(0);

  const [fuente, setFuente] = useState<FuenteType>('lista');
  const [listaOrigenCodigo, setListaOrigenCodigo] = useState('');
  const [listaOrigenSearchOpen, setListaOrigenSearchOpen] = useState(false);
  const [catalogoSearch, setCatalogoSearch] = useState('');
  const [ajuste, setAjuste] = useState('0');
  const [filtroRamo, setFiltroRamo] = useState('');
  const [filtroLinea, setFiltroLinea] = useState('');
  const [filtroClase, setFiltroClase] = useState('');

  const [previewItems, setPreviewItems] = useState<PreviewItem[]>([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);

  const ajusteNum = parseFloat(ajuste) || 0;
  const itemsIncluidos = previewItems.filter((i) => i.incluido);
  const todosIncluidos = previewItems.length > 0 && previewItems.every((i) => i.incluido);
  const algunosIncluidos = itemsIncluidos.length > 0 && !todosIncluidos;

  const updatePreviewItem = (codigoArticulo: string, patch: Partial<PreviewItem>) => {
    setPreviewItems((prev) =>
      prev.map((item) => (item.codigoArticulo === codigoArticulo ? { ...item, ...patch } : item))
    );
  };

  const toggleTodosIncluidos = (incluido: boolean) => {
    setPreviewItems((prev) => prev.map((item) => ({ ...item, incluido })));
  };

  const fetchPreviewDesdeList = async (): Promise<PreviewItem[]> => {
    if (!listaOrigenCodigo.trim()) {
      enqueueSnackbar('Seleccioná la lista origen', { variant: 'warning' });
      return [];
    }

    const pageSize = 50;
    let pageNumber = 1;
    let totalPages = 1;
    const allItems: ArticuloXListaPrecio[] = [];

    while (pageNumber <= totalPages) {
      const response = await articuloXListaPrecioService.getArticuloXListaPrecios({
        pageNumber, pageSize,
        codigoLista: listaOrigenCodigo.trim(),
        codigoRamo: filtroRamo.trim() || undefined,
        codigoLinea: filtroLinea.trim() || undefined,
        codigoClase: filtroClase.trim() || undefined,
      });
      allItems.push(...response.items);
      totalPages = response.totalPages || 1;
      pageNumber++;
    }

    return allItems.map((a) => {
      const usePorcentaje = a.porcentaje !== undefined && a.porcentaje !== null && Number(a.porcentaje) !== 0;
      if (usePorcentaje) {
        return {
          codigoArticulo: a.codigoArticulo,
          descripcion: a.descripcion,
          costoBase: undefined,
          porcentajeCalculado: Number(a.porcentaje) + ajusteNum,
          modo: 'PORCENTAJE' as const,
          incluido: true,
        };
      }
      const base = Number(a.precioVta || 0);
      return {
        codigoArticulo: a.codigoArticulo,
        descripcion: a.descripcion,
        costoBase: base,
        precioCalculado: base * (1 + ajusteNum / 100),
        modo: 'PRECIO' as const,
        incluido: true,
      };
    });
  };

  const fetchPreviewDesdeCatalogo = async (): Promise<PreviewItem[]> => {
    const pageSize = 50;
    let pageNumber = 1;
    let totalPages = 1;
    const allProducts: Producto[] = [];

    while (pageNumber <= totalPages) {
      const response = await productoService.getProductos({
        pageNumber, pageSize,
        searchTerm: catalogoSearch.trim() || undefined,
        ...PARAMS_PRODUCTO_OPERATIVO,
      });
      allProducts.push(...response.items);
      totalPages = response.totalPages || 1;
      pageNumber++;
    }

    return allProducts.map((p) => {
      const precioNeto = Number(p.precioVenta ?? 0);
      const costoStock = Number(p.costo ?? 0);
      return {
        codigoArticulo: p.codigoArticulo,
        descripcion: p.descripcion,
        costoBase: precioNeto,
        precioCostoCatalogo: costoStock,
        precioCalculado: precioNeto > 0 ? precioNeto * (1 + ajusteNum / 100) : undefined,
        porcentajeCalculado: ajusteNum !== 0 ? ajusteNum : undefined,
        modo: ajusteNum !== 0 && precioNeto === 0 ? 'PORCENTAJE' : 'PRECIO' as const,
        incluido: true,
      };
    });
  };

  const fetchPreview = async () => {
    setLoadingPreview(true);
    try {
      const items = fuente === 'lista'
        ? await fetchPreviewDesdeList()
        : await fetchPreviewDesdeCatalogo();

      if (items.length === 0) {
        enqueueSnackbar('No se encontraron artículos con los filtros seleccionados', { variant: 'warning' });
        return;
      }
      setPreviewItems(items);
      setStep(1);
    } catch {
      enqueueSnackbar('Error al obtener artículos', { variant: 'error' });
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleConfirm = async () => {
    setImporting(true);
    setImportProgress(0);
    let creados = 0;
    let actualizados = 0;
    let fallos = 0;

    const aImportar = previewItems.filter((i) => i.incluido);
    for (let i = 0; i < aImportar.length; i++) {
      const item = aImportar[i];
      const usePrecio = item.modo === 'PRECIO' && item.precioCalculado !== undefined;
      const payload: ArticuloXListaPrecio = {
        codigoLista: codigoListaDestino,
        codigoArticulo: item.codigoArticulo,
        descripcion: item.descripcion,
        precioVta: usePrecio ? item.precioCalculado : undefined,
        precioCosto: (item.precioCostoCatalogo ?? item.costoBase) ?? 0,
        porcentaje: !usePrecio ? item.porcentajeCalculado : undefined,
        propio: 'N',
        iva: 'S',
        fleteFp: 'N',
        codigoRamo: '',
        nombreRamo: '',
        codigoLinea: '',
        nombreLinea: '',
        codigoClase: '',
        nombreClase: '',
      };

      try {
        let existing: ArticuloXListaPrecio | null = null;
        try {
          existing = await articuloXListaPrecioService.getArticuloXListaPrecio(codigoListaDestino, item.codigoArticulo);
        } catch (err: any) {
          if (err?.response?.status !== 404) throw err;
        }

        if (existing) {
          await articuloXListaPrecioService.updateArticuloXListaPrecio(
            codigoListaDestino, item.codigoArticulo, { ...existing, ...payload }
          );
          actualizados++;
        } else {
          await articuloXListaPrecioService.createArticuloXListaPrecio(payload);
          creados++;
        }
      } catch {
        fallos++;
      }
      setImportProgress(Math.round(((i + 1) / aImportar.length) * 100));
    }

    enqueueSnackbar(
      fallos > 0
        ? `Completado. Creados: ${creados}. Actualizados: ${actualizados}. Fallos: ${fallos}.`
        : `Completado. Creados: ${creados}. Actualizados: ${actualizados}.`,
      { variant: fallos > 0 ? 'warning' : 'success' }
    );
    setImporting(false);
    onSuccess();
    handleClose();
  };

  const handleClose = () => {
    if (importing) return;
    setStep(0);
    setFuente('lista');
    setListaOrigenCodigo('');
    setCatalogoSearch('');
    setAjuste('0');
    setFiltroRamo('');
    setFiltroLinea('');
    setFiltroClase('');
    setPreviewItems([]);
    setImportProgress(0);
    onClose();
  };

  const canPreview = fuente === 'lista' ? !!listaOrigenCodigo.trim() : true;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight={700}>Agregar artículos en masa</Typography>
          <Typography variant="body2" color="text.secondary">Destino: {codigoListaDestino}</Typography>
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={step} sx={{ mb: 3, mt: 1 }}>
            <Step><StepLabel>Configuración</StepLabel></Step>
            <Step><StepLabel>Vista previa</StepLabel></Step>
          </Stepper>

          {step === 0 && (
            <Stack spacing={3}>
              {/* Source selector */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>Fuente de artículos</Typography>
                <ToggleButtonGroup
                  value={fuente}
                  exclusive
                  onChange={(_, val) => { if (val) setFuente(val); }}
                  size="small"
                  fullWidth
                >
                  <ToggleButton value="lista" sx={{ gap: 1 }}>
                    <ListIcon fontSize="small" />
                    Otra lista de precios
                  </ToggleButton>
                  <ToggleButton value="catalogo" sx={{ gap: 1 }}>
                    <CatalogIcon fontSize="small" />
                    Catálogo de productos
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Source-specific fields */}
              {fuente === 'lista' ? (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Lista origen *</Typography>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      value={listaOrigenCodigo}
                      onChange={(e) => setListaOrigenCodigo(e.target.value.toUpperCase())}
                      placeholder="Código de lista origen"
                      fullWidth
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button variant="outlined" onClick={() => setListaOrigenSearchOpen(true)} sx={{ minWidth: 100 }}>
                      Buscar
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <TextField
                  label="Buscar en catálogo (opcional)"
                  value={catalogoSearch}
                  onChange={(e) => setCatalogoSearch(e.target.value)}
                  placeholder="Código, descripción, varias palabras..."
                  fullWidth
                  size="small"
                  helperText="Podés usar varias palabras separadas por espacio; deben coincidir todas (en cualquier campo). Dejá vacío para traer todos los artículos del catálogo."
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}

              {/* Ajuste % */}
              <TextField
                label="% de ajuste"
                value={ajuste}
                onChange={(e) => setAjuste(e.target.value)}
                type="number"
                helperText={
                  fuente === 'lista'
                    ? 'Positivo = incremento, negativo = descuento. Se aplica sobre el precio o porcentaje de la lista origen.'
                    : 'Se aplica sobre el precio neto del catálogo (sin IVA). Sin precio neto, con % ≠ 0 se guarda solo el margen; sin ambos, precio $0.'
                }
                fullWidth
                size="small"
                inputProps={{ step: '0.01' }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

              {/* Group filters (only for lista source) */}
              {fuente === 'lista' && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Filtrar por agrupación (opcional)</Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField label="Código Ramo" value={filtroRamo} onChange={(e) => setFiltroRamo(e.target.value)} fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                    <TextField label="Código Línea" value={filtroLinea} onChange={(e) => setFiltroLinea(e.target.value)} fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                    <TextField label="Código Clase" value={filtroClase} onChange={(e) => setFiltroClase(e.target.value)} fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Stack>
                </Box>
              )}
            </Stack>
          )}

          {step === 1 && (
            <Box>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
                <Chip label={`${itemsIncluidos.length} de ${previewItems.length} artículos`} color="primary" size="small" />
                <Chip
                  label={`Ajuste: ${ajusteNum >= 0 ? '+' : ''}${ajusteNum}%`}
                  color={ajusteNum >= 0 ? 'success' : 'warning'}
                  size="small"
                />
                {fuente === 'lista' && <Chip label={`Desde: ${listaOrigenCodigo}`} size="small" />}
                {fuente === 'catalogo' && <Chip label="Desde: Catálogo" size="small" />}
              </Stack>

              {importing && (
                <Box sx={{ mb: 2 }}>
                  <LinearProgress variant="determinate" value={importProgress} />
                  <Typography variant="caption" color="text.secondary">{importProgress}% procesado</Typography>
                </Box>
              )}

              <TableContainer sx={{ maxHeight: 340, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                      <TableCell padding="checkbox" sx={{ fontWeight: 600 }}>
                        <Checkbox
                          size="small"
                          checked={todosIncluidos}
                          indeterminate={algunosIncluidos}
                          onChange={(_, checked) => toggleTodosIncluidos(checked)}
                          disabled={importing}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Artículo</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                      {fuente === 'catalogo' && <TableCell sx={{ fontWeight: 600 }}>Costo inv.</TableCell>}
                      <TableCell sx={{ fontWeight: 600 }}>
                        {fuente === 'lista' ? 'Precio/% Origen' : 'Precio neto'}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Precio a importar</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewItems.map((item) => (
                      <TableRow
                        key={item.codigoArticulo}
                        hover
                        sx={{ opacity: item.incluido ? 1 : 0.5 }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={item.incluido}
                            onChange={(_, checked) => updatePreviewItem(item.codigoArticulo, { incluido: checked })}
                            disabled={importing}
                          />
                        </TableCell>
                        <TableCell>{item.codigoArticulo}</TableCell>
                        <TableCell>{item.descripcion}</TableCell>
                        {fuente === 'catalogo' && (
                          <TableCell>
                            {item.precioCostoCatalogo != null && item.precioCostoCatalogo > 0
                              ? `$${item.precioCostoCatalogo.toFixed(2)}`
                              : <Typography variant="caption" color="text.disabled">Sin costo</Typography>}
                          </TableCell>
                        )}
                        <TableCell>
                          {fuente === 'lista'
                            ? (item.modo === 'PORCENTAJE'
                              ? `${Number((item.porcentajeCalculado ?? 0) - ajusteNum).toFixed(2)}%`
                              : `$${Number(item.costoBase || 0).toFixed(2)}`)
                            : (item.costoBase ? `$${item.costoBase.toFixed(2)}` : '—')}
                        </TableCell>
                        <TableCell>
                          {item.modo === 'PORCENTAJE' ? (
                            <TextField
                              type="number"
                              size="small"
                              value={item.porcentajeCalculado ?? ''}
                              onChange={(e) => {
                                const val = e.target.value === '' ? undefined : parseFloat(e.target.value);
                                updatePreviewItem(item.codigoArticulo, { porcentajeCalculado: val });
                              }}
                              disabled={importing || !item.incluido}
                              inputProps={{ step: '0.01', min: 0 }}
                              InputProps={{ endAdornment: <Typography variant="caption" color="text.secondary">%</Typography> }}
                              sx={{ width: 110, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                            />
                          ) : (
                            <TextField
                              type="number"
                              size="small"
                              value={item.precioCalculado ?? ''}
                              onChange={(e) => {
                                const val = e.target.value === '' ? undefined : parseFloat(e.target.value);
                                updatePreviewItem(item.codigoArticulo, { precioCalculado: val });
                              }}
                              disabled={importing || !item.incluido}
                              inputProps={{ step: '0.01', min: 0 }}
                              InputProps={{ startAdornment: <Typography variant="caption" color="text.secondary">$</Typography> }}
                              sx={{ width: 130, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {fuente === 'catalogo' && previewItems.some((i) => i.modo === 'PRECIO' && !(Number(i.costoBase) > 0)) && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Los artículos sin precio neto se guardarán con {ajusteNum !== 0 ? `${ajusteNum}% de margen` : 'precio $0'}.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} color="inherit" disabled={importing}>Cancelar</Button>
          {step === 1 && (
            <Button onClick={() => setStep(0)} color="inherit" disabled={importing}>Atrás</Button>
          )}
          {step === 0 && (
            <Button
              variant="contained"
              onClick={fetchPreview}
              disabled={loadingPreview || !canPreview}
              startIcon={loadingPreview ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              Previsualizar
            </Button>
          )}
          {step === 1 && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirm}
              disabled={importing || itemsIncluidos.length === 0}
            >
              {importing ? `Importando... ${importProgress}%` : `Confirmar (${itemsIncluidos.length})`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ListaPrecioSearch
        open={listaOrigenSearchOpen}
        onClose={() => setListaOrigenSearchOpen(false)}
        onSelect={(lista: any) => {
          setListaOrigenCodigo(lista.codigoLista);
          setListaOrigenSearchOpen(false);
        }}
      />
    </>
  );
};

export default AgregarArticulosMasivoDialog;
